<template>
  <div v-if="display" class="reference-inline">
    <span class="reference-icon">
      <span v-if="typeof display.icon === 'string'" class="reference-emoji">
        {{ display.icon }}
      </span>
      <component :is="display.icon" v-else />
    </span>
    <span class="reference-title">{{ display.title }}</span>
  </div>
</template>

<script lang="ts">
import { Component, defineComponent, PropType, toRef } from "vue";

import type {
  ReferenceId,
  ReferenceKind,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import { getTypeIcon } from "@/components/databases";
import CubeIcon from "@/components/icons/CubeIcon.vue";
import DatabaseIcon from "@/components/icons/DatabaseIcon.vue";
import EventIcon from "@/components/icons/EventIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import WorkspaceIcon from "@/components/icons/WorkspaceIcon.vue";
import * as inject from "@/components/inject";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import {
  isResolvableReferenceKind,
  referencePresentation,
} from "@/references/presentation";
import { resolveReferenceTitle } from "@/references/resolve";
import { nonReactiveIcon } from "@/utils/icon";

type Display = { title: string; icon: Component | string };

// Kind-specific icons; `table_ref` resolves its database's type icon in
// setup instead (this value is its fallback), and task / object references
// are normally rendered by TaskInline / CustomObjectInline rather than this
// component (see `PreviewedObject`).
function iconOfKind(kind: ReferenceKind): Component {
  switch (kind) {
    case "event":
    case "event-rec":
      return EventIcon;
    case "page":
      return PageIcon;
    case "people":
      return PersonIcon;
    case "task":
    case "task-rec":
      return TaskIcon;
    case "object":
      return CubeIcon;
    case "workspace_ref":
      return WorkspaceIcon;
    case "table_ref":
      return DatabaseIcon;
    case "organization":
    case "agent":
    case "automation":
    case "conversation":
    case "automation_run":
      return referencePresentation(kind).icon;
  }
}

/**
 * A compact single-line preview of any reference target (icon + title), the
 * generic sibling of TaskInline's preview mode and CustomObjectInline —
 * used where notes references are surfaced outside the editor (e.g. agenda
 * event cards). Renders nothing when the target no longer exists. The title
 * is a resolved snapshot (see `resolveReferenceTitle`), not a live query: a
 * concurrent rename shows up on the next re-mount or reference change.
 */
export default defineComponent({
  name: "ReferenceInline",
  props: {
    reference: {
      type: Object as PropType<ReferenceId>,
      required: true,
    },
  },
  async setup(props) {
    const reference = toRef(props, "reference");
    const controller = inject.controller();
    const profile = inject.profile();
    const releaser = useReleaser();

    const display = await releaser.add(
      bindAsync(
        reference,
        async (ref): Promise<ReleasableRef<Display | null>> => {
          const resolved = await resolveReferenceTitle(controller, ref);
          if (!resolved.exists) return releasableRef(null);
          const title =
            resolved.title ??
            (isResolvableReferenceKind(ref.kind)
              ? referencePresentation(ref.kind).fallbackTitle
              : "Untitled");
          if (ref.kind === "table_ref") {
            const { workspace_id: workspaceId, table_id: tableId } =
              await controller.id.tableRef.parse(ref.id);
            const database = await controller.graph.database.get(
              workspaceId,
              tableId
            );
            return database.map<Display>((db) => ({
              title,
              icon: nonReactiveIcon(
                getTypeIcon(profile.value, db, DatabaseIcon)
              ),
            }));
          }
          return releasableRef<Display | null>({
            title,
            icon: nonReactiveIcon(iconOfKind(ref.kind)),
          });
        }
      )
    );
    return { display };
  },
});
</script>

<style scoped lang="scss">
// Same compact row layout as CustomObjectInline.
.reference-inline {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 17px;
  column-gap: 4px;

  .reference-icon {
    @extend %font-small-regular;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
    @include themed() {
      color: t($grey-50);
    }

    :deep(svg),
    :deep(img) {
      width: 12px;
      height: 12px;
    }

    :deep(.fill) {
      @include themed() {
        fill: t($grey-50);
      }
    }

    :deep(.stroke) {
      @include themed() {
        stroke: t($grey-50);
      }
    }
  }

  .reference-title {
    @extend %font-small-regular;
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
